import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Coffee,
  Download,
  Droplets,
  Filter,
  Flame,
  Gift,
  CreditCard,
  Banknote,
  ShieldCheck,
  Globe,
  Layers,
  MapPin,
  MessageSquare,
  Package,
  Settings,
  Sparkles,
  Tag,
  Waves,
  Wifi,
  WifiOff,
  Zap,
} from 'lucide-react';
import { ANNUAL_BREWER_COUPON, ADD_ON_CUP_RATE, Plan, weeklyDoseCount } from './pricing';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type BrewerCategory = {
  id: string;
  name: string;
  description: string;
  options: string[];
  icon: ReactNode;
};

type CollectiveOption = {
  id: string;
  name: string;
  weeklyPrice: number;
  roasters: string[];
  desc: string;
  icon: ReactNode;
};

type PaymentMethod = 'gateway' | 'cash' | 'free';

type FormData = {
  brewer: string;
  brewerCategory: string;
  grinder: string;
  grindSize: number;
  collective: CollectiveOption | null;
  selectedCoffee: { name: string; roaster: string } | null;
  cupsPerDay: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  address: string;
  city: string;
  pincode: string;
  paymentMethod: PaymentMethod;
  billingCycle: Plan;
  preferences: {
    blend: string;
    varietal: string;
    process: string;
    roast: number;
    region: string;
    specialty: string;
  };
};

const storageKey = 'virtual-grinder-latest-draft-v3';

const defaultForm: FormData = {
  brewer: '',
  brewerCategory: '',
  grinder: 'HeyCafe 880s (Kolkata)',
  grindSize: 5,
  collective: null,
  selectedCoffee: null,
  cupsPerDay: 1,
  customerName: '',
  customerPhone: '',
  customerEmail: '',
  address: '',
  city: '',
  pincode: '',
  paymentMethod: 'gateway',
  billingCycle: 'weekly',
  preferences: {
    blend: 'Arabica',
    varietal: 'Standard Sln.9',
    process: 'Washed',
    roast: 3,
    region: 'Chikkamagalur',
    specialty: 'None',
  },
};

const brewerCategories: BrewerCategory[] = [
  { id: 'dripper', name: 'Dripper', icon: <Droplets />, description: 'Precision pour-overs', options: ['Hario V60', 'Kalita Wave', 'Chemex', 'Origami Dripper'] },
  { id: 'immersion', name: 'Immersion', icon: <Waves />, description: 'Full-bodied extraction', options: ['French Press', 'Aeropress', 'Clever Dripper', 'Siphon'] },
  { id: 'espresso', name: 'Espresso', icon: <Zap />, description: 'Pressure & intensity', options: ['Moka Pot', 'Manual Espresso', 'Home Espresso Machine'] },
  { id: 'cold-brew', name: 'Cold Brew', icon: <Flame />, description: 'Slow & chilled', options: ['Immersion Jug', 'Toddy System', 'Mizudashi Bottle', 'Mason Jar'] },
  { id: 'no-instrument', name: 'Instrument-Free', icon: <Package />, description: 'Brew anywhere', options: ['Drip Bags', 'Cold Brew Packs', 'Steeped Coffee'] },
];

const mainCollectives: CollectiveOption[] = [
  { id: 'worldwide', name: 'Worldwide Collective', weeklyPrice: 7000, roasters: ['Gardelli', 'Manhattan', 'The Barn'], icon: <Globe />, desc: 'The absolute pinnacle of world-class specialty coffee.' },
  { id: 'indian', name: 'Indian Coffees & Roasters', weeklyPrice: 2299, roasters: ['Blue Tokai', 'Subko', 'Naivo'], icon: <Sparkles />, desc: 'Curated selection from the best Indian estates and roasters.' },
  { id: 'daily', name: 'Daily Driver', weeklyPrice: 999, roasters: ['The Coffee Solutions Co.'], icon: <Coffee />, desc: 'Consistency for your everyday ritual. Fully configurable.' },
];

const configOptions = {
  blends: ['Arabica', 'Robusta', 'Liberica'],
  varietals: [
    { name: 'Standard Sln.9', priceMod: 0 },
    { name: 'SL79 (Premium)', priceMod: 150 },
    { name: 'CxR (High Yield)', priceMod: 50 },
    { name: 'Cauvery', priceMod: 100 },
  ],
  processes: [
    { name: 'Washed', priceMod: 0 },
    { name: 'Natural', priceMod: 50 },
    { name: 'Honey Sundried', priceMod: 100 },
    { name: 'Specialty Fermentation', priceMod: 250 },
  ],
  regions: ['Coorg', 'Chikkamagalur', 'Araku', 'Nagpur'],
  subOptions: ['None', 'GI Tagged', 'MNEB', 'Kappi Royale', 'Single Estate Origin'],
};

function loadDraft(): { step: number; formData: FormData; couponCode: string; discountApplied: number } {
  const fallback = { step: 1, formData: defaultForm, couponCode: '', discountApplied: 0 };
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return fallback;
    const parsed = JSON.parse(saved) as Partial<typeof fallback>;
    return {
      step: typeof parsed.step === 'number' ? Math.max(1, Math.min(7, parsed.step)) : fallback.step,
      formData: { ...defaultForm, ...(parsed.formData || {}), preferences: { ...defaultForm.preferences, ...(parsed.formData?.preferences || {}) } },
      couponCode: parsed.couponCode || '',
      discountApplied: typeof parsed.discountApplied === 'number' ? parsed.discountApplied : 0,
    };
  } catch {
    localStorage.removeItem(storageKey);
    return fallback;
  }
}

function isDeliveryReady(formData: FormData) {
  return (
    formData.customerName.trim().length >= 2
    && /^\d{10}$/.test(formData.customerPhone.replace(/\D/g, ''))
    && /^\S+@\S+\.\S+$/.test(formData.customerEmail.trim())
    && formData.address.trim().length >= 8
    && formData.city.trim().length >= 2
    && /^\d{6}$/.test(formData.pincode.trim())
  );
}

function parseAddress(formData: FormData) {
  const city = formData.city.trim();
  return {
    fullName: formData.customerName.trim(),
    phone: formData.customerPhone.replace(/\D/g, ''),
    email: formData.customerEmail.trim().toLowerCase(),
    line1: formData.address.trim().slice(0, 180),
    line2: '',
    city,
    state: city.toLowerCase().includes('mumbai') ? 'Maharashtra' : city.toLowerCase().includes('delhi') ? 'Delhi' : 'Karnataka',
    pincode: formData.pincode.trim(),
  };
}

function App() {
  const draft = useMemo(() => loadDraft(), []);
  const [step, setStep] = useState(draft.step);
  const [selectedCategory, setSelectedCategory] = useState<BrewerCategory | null>(null);
  const [couponCode, setCouponCode] = useState(draft.couponCode);
  const [discountApplied, setDiscountApplied] = useState(draft.discountApplied);
  const [suggestedMethod, setSuggestedMethod] = useState('');
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [formData, setFormData] = useState<FormData>(draft.formData);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installedHint, setInstalledHint] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [catalogSource, setCatalogSource] = useState<'database' | 'fallback'>('fallback');
  const [submitting, setSubmitting] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [orderId, setOrderId] = useState('');

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({ step, formData, couponCode, discountApplied }));
  }, [couponCode, discountApplied, formData, step]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/health')
      .then((response) => {
        if (!response.ok) throw new Error('API unavailable');
        return response.json();
      })
      .then((body: { ok?: boolean; db?: boolean }) => {
        if (!cancelled && body.ok && body.db) setCatalogSource('database');
      })
      .catch(() => setCatalogSource('fallback'));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const syncOnline = () => setOnline(navigator.onLine);
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('online', syncOnline);
    window.addEventListener('offline', syncOnline);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('online', syncOnline);
      window.removeEventListener('offline', syncOnline);
    };
  }, []);

  const varietalMod = configOptions.varietals.find((v) => v.name === formData.preferences.varietal)?.priceMod || 0;
  const processMod = configOptions.processes.find((p) => p.name === formData.preferences.process)?.priceMod || 0;
  const weeklyUnit = (formData.collective?.weeklyPrice || 0) + (formData.collective?.id === 'daily' ? varietalMod + processMod : 0);
  const weeklyWithCups = Math.round(weeklyUnit * (1 + (Math.max(1, formData.cupsPerDay) - 1) * ADD_ON_CUP_RATE));
  const fullAnnual = weeklyWithCups * 52;
  const totalBeforeDiscount = formData.billingCycle === 'weekly' ? weeklyWithCups : formData.billingCycle === 'monthly' ? weeklyWithCups * 4 : weeklyWithCups * 42;
  const total = Math.max(0, totalBeforeDiscount - discountApplied);
  const effectiveDueToday = formData.paymentMethod === 'free' ? 0 : total;
  const isDaily = formData.collective?.id === 'daily';
  const finalStep = 7;

  const canEnterStep = (targetStep: number) => {
    if (targetStep <= step) return true;
    if (targetStep === 2) return Boolean(formData.brewer || suggestedMethod.trim());
    if (targetStep === 3) return Boolean(formData.brewer || suggestedMethod.trim());
    if (targetStep === 4) return formData.collective?.id === 'daily';
    if (targetStep === 5) return formData.collective?.id === 'daily';
    if (targetStep === 6) return Boolean(formData.collective) && (!isDaily || Boolean(formData.selectedCoffee));
    if (targetStep === 7) return Boolean(formData.collective) && isDeliveryReady(formData);
    return false;
  };

  const applyCoupon = () => setDiscountApplied(couponCode.trim().toLowerCase() === 'grounded' ? 750 : 0);
  const nextStep = () => {
    if (step === 3 && !isDaily) return setStep(6);
    setStep((value) => Math.min(value + 1, finalStep));
  };
  const prevStep = () => {
    if (step === 1 && (selectedCategory || isSuggesting)) {
      setSelectedCategory(null);
      setIsSuggesting(false);
    } else if (step === 6 && !isDaily) {
      setStep(3);
    } else {
      setStep((value) => Math.max(value - 1, 1));
    }
  };

  const handleInstall = async () => {
    if (!installPrompt) {
      setInstalledHint(true);
      return;
    }
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const submitOrder = async () => {
    if (!formData.collective || !isDeliveryReady(formData)) return;
    setOrderError('');
    setSubmitting(true);
    try {
      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brewerId: 'free',
          grinderId: 'heycafe-880s',
          grindSize: formData.grindSize,
          collectiveId: formData.collective.id,
          cupsPerDay: formData.cupsPerDay,
          plan: formData.billingCycle,
          dueToday: effectiveDueToday,
          weeklyDoses: weeklyDoseCount(formData.cupsPerDay),
          paymentMethod: formData.paymentMethod,
          address: parseAddress(formData),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.orderId) throw new Error(body.errors?.join(', ') || 'Could not reserve subscription');
      localStorage.removeItem(storageKey);
      setOrderId(body.orderId);
    } catch (error) {
      setOrderError(error instanceof Error ? error.message : 'Could not reserve subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const progressWidth = `${Math.round((step / finalStep) * 100)}%`;

  if (orderId) {
    return (
      <main className="screen success">
        <section className="card success-card">
          <CheckCircle2 size={58} />
          <p className="eyebrow">Subscription locked</p>
          <h1>Virtual Grinder Live</h1>
          <p>Your calibrated coffee subscription is reserved. Your weekly dose card will carry brewer, grinder, roast and profile notes.</p>
          <div className="summary">
            <b>Order {orderId}</b>
            <span>{formData.collective?.name} · {formData.brewer || suggestedMethod || 'Instrument-free'} · {formData.cupsPerDay} cup/day</span>
            <span>Due today: ₹{effectiveDueToday.toLocaleString('en-IN')} · Payment: {formData.paymentMethod}</span>
          </div>
          <div className="success-actions">
            <button onClick={() => window.location.reload()}>Create another plan</button>
            <button className="secondary" onClick={() => {
              const blob = new Blob([JSON.stringify({ orderId, formData, total: effectiveDueToday }, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${orderId}.json`;
              link.click();
              URL.revokeObjectURL(url);
            }}><Download size={16} /> Download summary</button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="screen">
      <header className="hero compact-hero">
        <div className="hero-copy">
          <div className="brand-lockup">
            <img className="x8-logo" src="/brand/x8mylife-logo.svg" alt="x8mylife" />
            <p className="eyebrow">The Coffee Solutions Co. · HeyCafe 880s Kolkata</p>
          </div>
          <h1><span>Virtual Grinder</span> <span>Subscription Box</span></h1>
          <p>Freshly ground specialty coffee mapped to your brewer, calibration profile and weekly dose ritual.</p>
          <span className="db-note">Catalog source · {catalogSource === 'database' ? 'MySQL database' : 'offline fallback'}</span>
        </div>
        <div className="hero-actions">
          <span className={`status ${online ? 'online' : 'offline'}`}>{online ? <Wifi size={14} /> : <WifiOff size={14} />} {online ? 'Online' : 'Offline'}</span>
          <button className="install" onClick={handleInstall}>Install app</button>
        </div>
      </header>

      {installedHint && <div className="notice">Use your browser Share / Add to Home Screen option to install this PWA.</div>}

      <section className="app-shell latest-shell">
        <div className="mobile-card-header" aria-hidden="true">
          <div>
            <h1>Virtual Grinder</h1>
            <p>Specialty Coffee Subscription</p>
          </div>
          <span className="mobile-cup-mark"><Coffee size={30} /></span>
          <div className="mobile-progress-segments">
            {Array.from({ length: finalStep }).map((_, index) => <span key={index} className={index + 1 <= step ? 'active' : ''} />)}
          </div>
        </div>

        <aside className="sidebar">
          {['Method', 'Calibrate', 'Collective', 'Profile', 'Match', 'Delivery', 'Billing'].map((label, index) => {
            const targetStep = index + 1;
            const enabled = canEnterStep(targetStep);
            return (
              <button
                key={label}
                type="button"
                disabled={!enabled}
                aria-current={step === targetStep ? 'step' : undefined}
                className={step === targetStep ? 'active' : ''}
                onClick={() => enabled && setStep(targetStep)}
              >
                <span>{targetStep}</span>{label}
              </button>
            );
          })}
        </aside>

        <div className="panel">
          <div className="progress"><span style={{ width: progressWidth }} /></div>

          {step === 1 && (
            <section className="step">
              {!selectedCategory && !isSuggesting ? (
                <>
                  <h2><Layers /> Select Method</h2>
                  <p>Pick your brewer family or use our instrument-free brews. We will map grind and dose cards against the HeyCafe 880s baseline.</p>
                  <div className="grid method-grid">
                    {brewerCategories.map((category) => (
                      <button key={category.id} onClick={() => setSelectedCategory(category)} className="tile icon-tile">
                        <i>{category.icon}</i>
                        <b>{category.name}</b>
                        <small>{category.description}</small>
                        <span>{category.options.slice(0, 3).join(' · ')}</span>
                      </button>
                    ))}
                    <button onClick={() => setIsSuggesting(true)} className="tile icon-tile dashed-tile">
                      <i><MessageSquare /></i>
                      <b>Suggest Method</b>
                      <small>Can't find yours?</small>
                      <span>Tell us your brewer and we will calibrate a conversion profile.</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="step-card">
                  <button onClick={() => { setSelectedCategory(null); setIsSuggesting(false); }} className="link-button"><ChevronLeft size={14} /> Back to categories</button>
                  {isSuggesting ? (
                    <div className="form">
                      <h2><MessageSquare /> Recommend a Method</h2>
                      <input value={suggestedMethod} onChange={(event) => setSuggestedMethod(event.target.value)} placeholder="e.g. Vietnamese Phin" />
                      <button disabled={!suggestedMethod.trim()} onClick={() => { setFormData({ ...formData, brewer: suggestedMethod, brewerCategory: 'suggested' }); nextStep(); }} className="primary-wide">Submit method</button>
                    </div>
                  ) : selectedCategory && (
                    <>
                      <h2>{selectedCategory.name} Brewers</h2>
                      <div className="grid">
                        {selectedCategory.options.map((option) => (
                          <button key={option} onClick={() => { setFormData({ ...formData, brewer: option, brewerCategory: selectedCategory.id }); nextStep(); }} className="tile">
                            <small>{selectedCategory.name}</small><b>{option}</b><span>{selectedCategory.description}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </section>
          )}

          {step === 2 && (
            <section className="step calibration-step">
              <h2><Settings /> Verification & Calibration</h2>
              <p>Setting up HeyCafe 880s for {formData.brewer || 'your brewer'} with a visual grind knob and dose card.</p>
              <div className="knob-card">
                <div className="knob-wrap">
                  <div className="knob" style={{ transform: `rotate(${formData.grindSize * 27 - 135}deg)` }}><Settings size={42} /></div>
                  <div className="knob-readout"><b>{formData.grindSize}</b><span>Grind size</span></div>
                </div>
                <input min="1" max="10" step="1" type="range" value={formData.grindSize} onChange={(event) => setFormData({ ...formData, grindSize: Number(event.target.value) })} />
                <div className="scale"><span>Fine</span><b>HeyCafe 880s</b><span>Coarse</span></div>
              </div>
              <div className="actions"><button className="secondary" onClick={prevStep}><ChevronLeft size={16} /> Back</button><button onClick={() => setStep(3)}>Continue to selection <ChevronRight size={16} /></button></div>
            </section>
          )}

          {step === 3 && (
            <section className="step">
              <h2><Coffee /> Pick Your Collective</h2>
              <p>Choose a global flex, Indian roaster curation or a custom Daily Driver profile.</p>
              <div className="collectives">
                {mainCollectives.map((collective) => (
                  <button key={collective.id} onClick={() => setFormData({ ...formData, collective })} className={`collective ${formData.collective?.id === collective.id ? 'selected' : ''}`}>
                    <div><small>{collective.roasters.join(' · ')}</small><b>{collective.name}</b><p>{collective.desc}</p></div>
                    <strong>₹{collective.weeklyPrice.toLocaleString('en-IN')}/wk</strong>
                  </button>
                ))}
              </div>
              <div className="actions"><button className="secondary" onClick={prevStep}><ChevronLeft size={16} /> Back</button><button disabled={!formData.collective} onClick={() => setStep(formData.collective?.id === 'daily' ? 4 : 6)}>{isDaily ? 'Configure your coffee' : 'Enter delivery details'} <ChevronRight size={16} /></button></div>
            </section>
          )}

          {step === 4 && (
            <section className="step config-step">
              <h2><Filter /> Configure Daily Driver</h2>
              <p>Build your house cup. Pricing updates automatically for premium varietals and processing.</p>
              <div className="config-block"><label>Preferred Blend</label><div className="pill-row">{configOptions.blends.map((blend) => <button key={blend} className={formData.preferences.blend === blend ? 'selected-pill' : ''} onClick={() => setFormData({ ...formData, preferences: { ...formData.preferences, blend } })}>{blend}</button>)}</div></div>
              <div className="grid">
                {configOptions.varietals.map((varietal) => <button key={varietal.name} className={`tile ${formData.preferences.varietal === varietal.name ? 'selected' : ''}`} onClick={() => setFormData({ ...formData, preferences: { ...formData.preferences, varietal: varietal.name } })}><b>{varietal.name}</b><span>{varietal.priceMod ? `+₹${varietal.priceMod}/wk` : 'Included'}</span></button>)}
              </div>
              <div className="grid">
                {configOptions.processes.map((process) => <button key={process.name} className={`tile ${formData.preferences.process === process.name ? 'selected' : ''}`} onClick={() => setFormData({ ...formData, preferences: { ...formData.preferences, process: process.name } })}><b>{process.name}</b><span>{process.priceMod ? `+₹${process.priceMod}/wk` : 'Included'}</span></button>)}
              </div>
              <div className="knob-card compact"><label>Roast Level · {formData.preferences.roast}</label><input min="1" max="5" type="range" value={formData.preferences.roast} onChange={(event) => setFormData({ ...formData, preferences: { ...formData.preferences, roast: Number(event.target.value) } })} /><div className="scale"><span>Light</span><b>Medium</b><span>Dark</span></div></div>
              <div className="grid">{configOptions.regions.map((region) => <button key={region} className={`tile ${formData.preferences.region === region ? 'selected' : ''}`} onClick={() => setFormData({ ...formData, preferences: { ...formData.preferences, region } })}><b>{region}</b><span>Origin selection</span></button>)}</div>
              <div className="actions"><button className="secondary" onClick={prevStep}><ChevronLeft size={16} /> Back</button><button onClick={nextStep}>Choose first coffee <ChevronRight size={16} /></button></div>
            </section>
          )}

          {step === 5 && (
            <section className="step">
              <h2><Sparkles /> Matches for Your Profile</h2>
              <p>Based on your filters, these {formData.preferences.process} processed {formData.preferences.blend} coffees from {formData.preferences.region} are currently available.</p>
              <div className="collectives">
                {[1, 2, 3].map((index) => {
                  const coffee = { name: `Estate Choice #${index} - ${formData.preferences.region}`, roaster: 'Coffee Solutions' };
                  const selected = formData.selectedCoffee?.name === coffee.name;
                  return <button key={coffee.name} onClick={() => setFormData({ ...formData, selectedCoffee: coffee })} className={`collective ${selected ? 'selected' : ''}`}><div><small>{formData.preferences.varietal}</small><b>{coffee.name}</b><p>{formData.preferences.process} · roast level {formData.preferences.roast}</p></div>{selected && <Check />}</button>;
                })}
              </div>
              <div className="notice inline-notice">Selection #1 best matches your roast level and processing preference.</div>
              <div className="actions"><button className="secondary" onClick={prevStep}><ChevronLeft size={16} /> Back</button><button disabled={!formData.selectedCoffee} onClick={nextStep}>Enter delivery details <ChevronRight size={16} /></button></div>
            </section>
          )}

          {step === 6 && (
            <section className="step">
              <h2><MapPin /> Shipping & Referral</h2>
              <p>Coupon <b>GROUNDED</b> unlocks ₹750 off. Address is saved locally until you confirm.</p>
              <div className="form">
                <label>Coupon or referral code</label>
                <div className="coupon-row"><input value={couponCode} onChange={(event) => setCouponCode(event.target.value)} placeholder="Try GROUNDED" /><button onClick={applyCoupon}>Apply</button></div>
                {discountApplied > 0 && <p className="save"><Tag size={14} /> Discount of ₹{discountApplied} applied</p>}
                <div className="row">
                  <input aria-label="Full name" value={formData.customerName} onChange={(event) => setFormData({ ...formData, customerName: event.target.value })} placeholder="Full name" />
                  <input aria-label="Mobile number" inputMode="numeric" maxLength={10} value={formData.customerPhone} onChange={(event) => setFormData({ ...formData, customerPhone: event.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="10-digit mobile" />
                </div>
                <input aria-label="Email" type="email" value={formData.customerEmail} onChange={(event) => setFormData({ ...formData, customerEmail: event.target.value })} placeholder="Email for receipt" />
                <label>Shipping Address</label>
                <textarea aria-invalid={Boolean(formData.address && formData.address.trim().length < 8)} value={formData.address} onChange={(event) => setFormData({ ...formData, address: event.target.value })} placeholder="House / office, street, landmark" />
                <div className="row">
                  <input aria-label="City" value={formData.city} onChange={(event) => setFormData({ ...formData, city: event.target.value })} placeholder="City" />
                  <input aria-label="Pincode" inputMode="numeric" maxLength={6} value={formData.pincode} onChange={(event) => setFormData({ ...formData, pincode: event.target.value.replace(/\D/g, '').slice(0, 6) })} placeholder="Pincode" />
                </div>
                {!isDeliveryReady(formData) && <p className="error">Enter name, 10-digit phone, valid email, address, city and 6-digit pincode to continue.</p>}
              </div>
              <div className="actions"><button className="secondary" onClick={prevStep}><ChevronLeft size={16} /> Back</button><button disabled={!isDeliveryReady(formData)} onClick={() => setStep(7)}>Finalize billing <ChevronRight size={16} /></button></div>
            </section>
          )}

          {step === 7 && (
            <section className="step">
              <h2><Gift /> Review & Billing</h2>
              <p>Select weekly, monthly or annual billing. Annual includes the complimentary brewer coupon from The Coffee Solutions company.</p>
              <div className="plans">
                {[
                  { id: 'weekly' as Plan, name: 'Weekly Billing', desc: 'Charged every Monday' },
                  { id: 'monthly' as Plan, name: 'Monthly Billing', desc: '4 weeks charged upfront' },
                  { id: 'annual' as Plan, name: 'Annual Membership', desc: '42 paid weeks + brewer coupon' },
                ].map((plan) => <button key={plan.id} onClick={() => setFormData({ ...formData, billingCycle: plan.id })} className={`plan ${formData.billingCycle === plan.id ? 'selected' : ''}`}><b>{plan.name}</b><span>{plan.desc}</span><strong>{plan.id === 'annual' ? `₹${ANNUAL_BREWER_COUPON.toLocaleString('en-IN')} coupon` : 'Flexible'}</strong></button>)}
              </div>
              <h3 className="section-title">Payment mode</h3>
              <div className="payment-grid">
                {[
                  { id: 'gateway' as PaymentMethod, name: 'Payment Gateway', desc: 'Razorpay/UPI/card-ready order intent', icon: <CreditCard /> },
                  { id: 'cash' as PaymentMethod, name: 'Cash', desc: 'Collect cash on first delivery', icon: <Banknote /> },
                  { id: 'free' as PaymentMethod, name: 'Free / Sponsored', desc: 'Zero-rupee subscription for comped plans', icon: <ShieldCheck /> },
                ].map((method) => (
                  <button key={method.id} onClick={() => setFormData({ ...formData, paymentMethod: method.id })} className={`payment-card ${formData.paymentMethod === method.id ? 'selected' : ''}`}>
                    <i>{method.icon}</i><b>{method.name}</b><span>{method.desc}</span>
                  </button>
                ))}
              </div>
              <div className="checkout">
                <Gift /><div><b>{formData.collective?.name}</b><span>{formData.brewer || suggestedMethod} · {formData.cupsPerDay} cup/day · {weeklyDoseCount(formData.cupsPerDay)} doses/week</span>{isDaily && <span className="gift">{formData.preferences.blend} · {formData.preferences.varietal} · {formData.preferences.process}</span>}{formData.billingCycle === 'annual' && <span className="save">Full annual value ₹{fullAnnual.toLocaleString('en-IN')} · 10 weeks free</span>}</div><strong>₹{effectiveDueToday.toLocaleString('en-IN')}</strong>
              </div>
              {orderError && <p className="error">{orderError}</p>}
              <div className="actions"><button className="secondary" onClick={prevStep}><ChevronLeft size={16} /> Back</button><button disabled={submitting || !formData.collective || !isDeliveryReady(formData)} onClick={submitOrder}>{submitting ? 'Confirming...' : formData.paymentMethod === 'gateway' ? 'Create payment order' : formData.paymentMethod === 'cash' ? 'Reserve cash order' : 'Confirm free subscription'}</button></div>
            </section>
          )}
        </div>
        <div className="mobile-card-footer">Powered by The Coffee Solutions Co.</div>
      </section>
    </main>
  );
}

export default App;
